import { DateTime } from 'luxon'

export type DateWithFallback = string | DateTime

const isMidnight = (dt: DateTime): boolean => dt.hour === 0 && dt.minute === 0 && dt.second === 0

/**
 * A timestamp that carries no offset is UTC. The backend serialises its naive `LocalDateTime` columns
 * that way (SPIN `mintedAt`, `createdAt`, `assignedAt`), and `new Date()` reads those as *local* — which
 * displayed them shifted by the viewer's own offset.
 *
 * An offset that is present is kept rather than normalised away: for a customer-supplied sampling date it
 * records the zone the customer meant, which [formatDate] needs to tell a date-only value from a real
 * midnight instant. Unparsable input comes back unchanged so a caller can still show the raw server
 * value rather than a placeholder.
 */
export function parseDate(dateString: string): DateWithFallback {
  const parsed = DateTime.fromISO(dateString, { zone: 'utc', setZone: true })
  return parsed.isValid ? parsed : dateString
}

/**
 * Midnight means the value is date-only, and is rendered without a time in the zone it was expressed in
 * — printing the viewer's zone instead could move it to the previous day.
 *
 * Midnight is tested in that zone *and* in UTC because both spellings occur: the metadata CSV importer
 * parses its eleven date-only formats in the browser's zone, so a customer's `09.09.2026` arrives as
 * midnight at an offset, while a naive server column arrives as midnight UTC. Anything with a real time
 * of day is shown in the viewer's zone.
 */
export function formatDate(date: DateWithFallback): string {
  if (typeof date === 'string') {
    return date
  }
  if (!date.isValid) {
    return 'Invalid date'
  }

  if (isMidnight(date) || isMidnight(date.toUTC())) {
    return (isMidnight(date) ? date : date.toUTC()).toFormat('dd.MM.yyyy')
  }
  return date.toLocal().toFormat('dd.MM.yyyy, HH:mm')
}
